import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ErrorMessage } from '@hookform/error-message';
import { useDispatch, useSelector } from 'react-redux';
import Logo from '../../Assets/images/main-logo.svg';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Config } from '../../config/Config';
import { LanguageConstants } from '../../constants/LanguageConstants';
import LanguageDropdown from '../layout/LanguageDropdown';
import CommanPhoneNumber from '../layout/CommanPhoneNumber';
import BrandedLoaderOverlay from '../layout/BrandedLoaderOverlay';
import AuthSubmitButton from '../layout/AuthSubmitButton';
import CompleteProfileValidation from '../../Validation/CompleteProfileValidation';
import useMakeRequest from '../../utils/ApiHelper';
import { ApiConstants } from '../../constants/ApiConstants';
import { ApiTypes, StorageConstant } from '../../constants/Constants';
import { Routes as AppRoutes } from '../../constants/RouteConstants';
import { deleteFromStorage, getFromStorage, saveToStorage } from '../../utils/Helper';
import { invalidatePendingDashboardBootstrapCache } from '../../utils/pendingDashboardBootstrap';
import { ProfileAction } from '../../redux/Action/Authaction';
import {
  clearSocialProfilePrefill,
  clearSocialProfileTransition,
  enrichProfileUser,
  isSocialProfileTransitionPending,
  getPostSocialLoginPath,
  getPostSocialLoginRedirect,
  mergedBusinessProfile,
  needsSocialProfileCompletion,
  readSocialProfilePrefill,
  splitSocialDisplayName,
} from '../../utils/socialProfileCompletion';

type AccountType = 'individual' | 'business';
const ACCOUNT_TYPE_DROPDOWN_ID = 'completeProfileAccountTypeDropdown';

interface FormInputs {
  account_type: AccountType;
  first_name: string;
  last_name: string;
  company_name?: string;
  company_email?: string;
  email: string;
  phone_number: string;
  phone_country_code: string;
}

function CompleteProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profileFetch = useMakeRequest();
  const profileUpdate = useMakeRequest();
  const logoutRequest = useMakeRequest();
  const profileState = useSelector((state: any) => state?.ProfileState);
  const [isSubmitNavigating, setIsSubmitNavigating] = useState(false);
  const [isBackToLoginNavigating, setIsBackToLoginNavigating] = useState(false);
  const [socialTransitionActive, setSocialTransitionActive] = useState(() =>
    isSocialProfileTransitionPending(),
  );

  const token = getFromStorage({ key: StorageConstant?.ACCESS_TOKEN });
  const storedUser = getFromStorage({ key: 'user_data' });
  const profileUser = profileState?.data?.data ?? storedUser?.data ?? storedUser;

  const prefill = useMemo(() => readSocialProfilePrefill(), []);

  const {
    register,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      account_type: 'individual',
      first_name: '',
      last_name: '',
      company_name: '',
      company_email: '',
      email: '',
      phone_country_code: '+1',
      phone_number: '',
    },
    resolver: yupResolver(CompleteProfileValidation),
  });

  const accountType = watch('account_type');
  const boxImageSrc = `${process.env.PUBLIC_URL || ''}/signup/box image.png`;

  const socialProviderLabel = useMemo(() => {
    const type = String(prefill?.social_type || profileUser?.social_type || '').toLowerCase();
    if (type.includes('apple')) return 'Apple';
    return 'Google';
  }, [prefill?.social_type, profileUser?.social_type]);

  const pageTitle =
    (LanguageConstants as any)?.SOCIAL_PROFILE_TITLE?.replace?.('{provider}', socialProviderLabel) ??
    `Sign Up with ${socialProviderLabel.toLowerCase()}`;

  const signupSubtitle =
    accountType === 'business'
      ? LanguageConstants?.SIGNUP_SUBTITLE_BUSINESS
      : LanguageConstants?.SIGNUP_SUBTITLE_INDIVIDUAL;

  useEffect(() => {
    if (!token) return;
    profileFetch.makeRequest({ url: ApiConstants?.Profile, toastStatus: false });
    // @ts-ignore
    dispatch(ProfileAction(undefined));
  }, [token, dispatch]);

  useEffect(() => {
    if (!socialTransitionActive) return;
    const hasProfileData = Boolean(profileState?.data?.data ?? profileFetch?.data?.data);
    const ready =
      Boolean(profileUser?.email || hasProfileData) && !profileFetch.loading;
    if (ready) {
      clearSocialProfileTransition();
      setSocialTransitionActive(false);
    }
  }, [
    socialTransitionActive,
    profileState?.data?.data,
    profileFetch?.data?.data,
    profileFetch.loading,
    profileUser?.email,
  ]);

  useEffect(() => {
    const user = profileState?.data?.data ?? profileFetch?.data?.data;
    if (!user) return;

    const splitFromName = splitSocialDisplayName(
      prefill?.name || user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(' '),
    );
    const business = mergedBusinessProfile(user);

    reset({
      account_type:
        user?.account_type === 'business' || user?.account_type === 'individual'
          ? user.account_type
          : 'individual',
      first_name: (user?.first_name || prefill?.first_name || splitFromName.first_name || '').trim(),
      last_name: (user?.last_name || prefill?.last_name || splitFromName.last_name || '').trim(),
      email: (user?.email || prefill?.email || '').trim(),
      phone_country_code: user?.phone_country_code || '+1',
      phone_number: user?.phone_number || '',
      company_name: String(business?.company_name || '').trim(),
      company_email: String(
        business?.company_email || business?.companyEmail || user?.email || prefill?.email || '',
      ).trim(),
    });
  }, [profileState?.data?.data, profileFetch?.data?.data, prefill, reset]);

  const handleAccountTypeChange = (v: AccountType) => {
    setValue('account_type', v, { shouldValidate: true, shouldDirty: true });
    if (v === 'individual') {
      setValue('company_name', '');
      setValue('company_email', '');
      clearErrors(['company_name', 'company_email']);
    } else {
      const currentEmail = watch('email');
      if (!watch('company_email') && currentEmail) {
        setValue('company_email', currentEmail, { shouldValidate: true });
      }
    }
  };

  const accountTypeLabel =
    accountType === 'business'
      ? LanguageConstants?.ACCOUNT_TYPE_BUSINESS
      : LanguageConstants?.ACCOUNT_TYPE_INDIVIDUAL;

  const onSubmit = (data: FormInputs) => {
    if (profileUpdate.loading || isSubmitNavigating) return;

    const base: Record<string, unknown> = {
      account_type: data.account_type,
      phone_country_code: data.phone_country_code,
      phone_number: (data.phone_number || '').trim(),
    };

    if (data.account_type === 'business') {
      base.company_name = (data.company_name || '').trim();
      base.contact_name = [data.first_name.trim(), data.last_name.trim()].filter(Boolean).join(' ');
      base.email = (data.company_email || data.email || '').trim();
    } else {
      base.first_name = data.first_name.trim();
      base.last_name = data.last_name.trim();
      base.email = data.email.trim();
    }

    profileUpdate.makeRequest({
      payload: base,
      type: ApiTypes.POST,
      url: ApiConstants?.UpdateProfile,
      toastStatus: true,
    });
  };

  useEffect(() => {
    if (!profileUpdate?.status) return;
    const raw = profileUpdate?.data?.data;
    if (!raw) return;

    const updated = enrichProfileUser(raw);
    saveToStorage({
      key: 'user_data',
      value: {
        ...(profileUpdate?.data && typeof profileUpdate.data === 'object' ? profileUpdate.data : {}),
        data: updated,
      },
    });
    clearSocialProfilePrefill();
    // @ts-ignore
    dispatch(ProfileAction(undefined));

    if (!needsSocialProfileCompletion(updated)) {
      setIsSubmitNavigating(true);
      window.location.href = getPostSocialLoginRedirect(updated);
    }
  }, [profileUpdate?.status]);

  const handlePhoneNumber = (dialCode: string, nationalNumber: string) => {
    const d = String(dialCode ?? '').replace(/\D/g, '');
    setValue('phone_country_code', d ? `+${d}` : '');
    setValue('phone_number', nationalNumber ?? '');
  };
  const handleBackToLogin = async () => {
    if (isBackToLoginNavigating || logoutRequest.loading) return;

    setIsBackToLoginNavigating(true);
    clearSocialProfilePrefill();
    clearSocialProfileTransition();

    try {
      await logoutRequest.makeRequest({ url: ApiConstants?.LogOut, toastStatus: false });
    } catch {
      // Proceed with local logout even if the API call fails.
    }

    invalidatePendingDashboardBootstrapCache();
    deleteFromStorage({ key: StorageConstant?.ACCESS_TOKEN, type: 'local' });
    deleteFromStorage({ key: 'currencyValue', type: 'local' });
    deleteFromStorage({ key: 'eurCurrencyValue', type: 'local' });
    deleteFromStorage({ key: 'user_data', type: 'local' });
    window.location.href = `${Config?.BASE_URL}/login`;
  };

  if (!token) {
    return <Navigate to={AppRoutes.Login} replace />;
  }

  const resolvedUser = profileState?.data?.data ?? profileFetch?.data?.data ?? profileUser;
  if (resolvedUser && !needsSocialProfileCompletion(resolvedUser)) {
    return <Navigate to={getPostSocialLoginPath(resolvedUser)} replace />;
  }

  const showPageLoader =
    !socialTransitionActive &&
    profileFetch.loading &&
    !profileState?.data?.data &&
    !profileUser?.email;
  const showSignUpLoader = profileUpdate.loading || isSubmitNavigating;
  const showBrandedLoader = showSignUpLoader || socialTransitionActive || showPageLoader;
  const showPageShell = !socialTransitionActive;

  return (
    <>
      <BrandedLoaderOverlay active={showBrandedLoader} immediate />
      {showPageShell ? (
      <div className="auth-page auth-page--signup auth-page-complete-profile qqqqq">
        <div className="auth-split">
          <aside className="auth-promo" aria-hidden="false">
            <div className="auth-promo-top">
              <a className="auth-promo-logo" href={`${Config?.BASE_URL}/`}>
                <img src={Logo} alt="Quote N Buy" />
              </a>
              <div className="w-100 auth-promo-txt-wrap">
                <div className="auth-promo-text-slider">
                  <Swiper
                    className="auth-promo-text-swiper"
                    modules={[Autoplay, Pagination]}
                    slidesPerView={1}
                    loop
                    speed={700}
                    autoplay={{
                      delay: 1500,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    pagination={{ clickable: true }}
                  >
                    <SwiperSlide>
                      <div className="auth-promo-slide">
                        <h2 className="auth-promo-title">{LanguageConstants?.AUTH_PROMO_TITLE}</h2>
                        <p className="auth-promo-text">
                          {LanguageConstants?.WESPECIALIZETEXT ?? LanguageConstants?.AUTH_PROMO_TEXT}
                        </p>
                      </div>
                    </SwiperSlide>
                    <SwiperSlide>
                      <div className="auth-promo-slide">
                        <h2 className="auth-promo-title">
                          {LanguageConstants?.AUTH_PROMO_TITLE_2 ?? LanguageConstants?.AUTH_PROMO_TITLE}
                        </h2>
                        <p className="auth-promo-text">{LanguageConstants?.SIMPLYTEXT}</p>
                      </div>
                    </SwiperSlide>
                    <SwiperSlide>
                      <div className="auth-promo-slide">
                        <h2 className="auth-promo-title">
                          {LanguageConstants?.AUTH_PROMO_TITLE_3 ?? LanguageConstants?.AUTH_PROMO_TITLE}
                        </h2>
                        <p className="auth-promo-text">{LanguageConstants?.PERHAPSTEXT}</p>
                      </div>
                    </SwiperSlide>
                  </Swiper>
                </div>
              </div>
            </div>
            <img className="auth-promo-visual" src={boxImageSrc} alt="" />
          </aside>

          <div className="auth-form-wrap">
            <div className="auth-form-wrap-inner">
              <div className="auth-form-inner auth-form-inner-card">
                <h1 className="auth-title">{pageTitle}</h1>
                <p className="auth-head-para">{signupSubtitle}</p>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="com-input-wrap mb-3">
                    <label className="form-label" htmlFor="complete_profile_account_type">
                      <span className="input-star">*</span>
                      {LanguageConstants?.ACCOUNT_TYPE}
                    </label>
                    <input type="hidden" id="complete_profile_account_type" {...register('account_type')} />
                    <div className="dropdown">
                      <button
                        type="button"
                        className="form-select text-start dropdown-toggle signup-account-type-toggle"
                        id={ACCOUNT_TYPE_DROPDOWN_ID}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {accountTypeLabel}
                      </button>
                      <ul className="dropdown-menu w-100" aria-labelledby={ACCOUNT_TYPE_DROPDOWN_ID}>
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => handleAccountTypeChange('individual')}
                          >
                            {LanguageConstants?.ACCOUNT_TYPE_INDIVIDUAL}
                          </button>
                        </li>
                        <li>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => handleAccountTypeChange('business')}
                          >
                            {LanguageConstants?.ACCOUNT_TYPE_BUSINESS}
                          </button>
                        </li>
                      </ul>
                    </div>
                    <ErrorMessage
                      errors={errors}
                      name="account_type"
                      render={({ message }) => <p className="input-error">{message}</p>}
                    />
                  </div>

                  <div className="row">
                    <div className="col-xl-6">
                      <div className="com-input-wrap mb-3">
                        <label className="form-label" htmlFor="complete_profile_first_name">
                          <span className="input-star">*</span>
                          {LanguageConstants?.FIRST_NAME}
                        </label>
                        <input
                          id="complete_profile_first_name"
                          className="form-control"
                          type="text"
                          placeholder={LanguageConstants?.FIRST_NAME}
                          autoComplete="given-name"
                          {...register('first_name')}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="first_name"
                          render={({ message }) => <p className="input-error">{message}</p>}
                        />
                      </div>
                    </div>
                    <div className="col-xl-6">
                      <div className="com-input-wrap mb-3">
                        <label className="form-label" htmlFor="complete_profile_last_name">
                          <span className="input-star">*</span>
                          {LanguageConstants?.LAST_NAME}
                        </label>
                        <input
                          id="complete_profile_last_name"
                          className="form-control"
                          type="text"
                          placeholder={LanguageConstants?.LAST_NAME}
                          autoComplete="family-name"
                          {...register('last_name')}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="last_name"
                          render={({ message }) => <p className="input-error">{message}</p>}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-xl-6">
                      <div className="com-input-wrap mb-3">
                        {accountType === 'business' ? (
                          <>
                            <label className="form-label" htmlFor="complete_profile_company_email">
                              <span className="input-star">*</span>
                              {LanguageConstants?.COMPANY_EMAIL}
                            </label>
                            <input
                              id="complete_profile_company_email"
                              className="form-control"
                              type="email"
                              readOnly
                              aria-readonly="true"
                              autoComplete="email"
                              {...register('company_email')}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="company_email"
                              render={({ message }) => <p className="input-error">{message}</p>}
                            />
                          </>
                        ) : (
                          <>
                            <label className="form-label" htmlFor="complete_profile_email">
                              <span className="input-star">*</span>
                              {LanguageConstants?.LOGIN_EMAIL_ADDRESS}
                            </label>
                            <input
                              id="complete_profile_email"
                              className="form-control"
                              type="email"
                              readOnly
                              aria-readonly="true"
                              {...register('email')}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="col-xl-6">
                      <div className="com-input-wrap mb-3">
                        <label className="form-label" htmlFor="complete_profile_phone">
                          {LanguageConstants?.PHONE_NUMBER}{' '}
                          <span className="optional-mark">{LanguageConstants?.OPTIONAL_MARK}</span>
                        </label>
                        <div className="wrap-phn-code signup-phone-field">
                          <CommanPhoneNumber
                            hideLabel
                            handleMobileNumber={handlePhoneNumber}
                            value={watch('phone_country_code')}
                            nationalNumber={watch('phone_number')}
                          />
                          <input
                            id="complete_profile_phone"
                            className="wrap-phn-input"
                            type="text"
                            {...register('phone_number')}
                          />
                        </div>
                        <ErrorMessage
                          errors={errors}
                          name="phone_number"
                          render={({ message }) => <p className="input-error">{message}</p>}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="phone_country_code"
                          render={({ message }) => <p className="input-error">{message}</p>}
                        />
                      </div>
                    </div>
                  </div>

                  {accountType === 'business' ? (
                    <div className="row">
                      <div className="col-xl-6">
                        <div className="com-input-wrap mb-3">
                          <label className="form-label" htmlFor="complete_profile_company_name">
                            <span className="input-star">*</span>
                            {LanguageConstants?.COMPANY_NAME_LABEL}
                          </label>
                          <input
                            id="complete_profile_company_name"
                            className="form-control"
                            type="text"
                            placeholder={LanguageConstants?.COMPANY_NAME_PLACEHOLDER}
                            autoComplete="organization"
                            {...register('company_name')}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="company_name"
                            render={({ message }) => <p className="input-error">{message}</p>}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <AuthSubmitButton
                    className="w-100 form-submit-btn"
                    loading={showSignUpLoader}
                    disabled={showSignUpLoader || isBackToLoginNavigating}
                  >
                    {LanguageConstants?.SINGUP}
                  </AuthSubmitButton>
                  <div className="small-txt text-center mt-4 fw-normal">
                    {LanguageConstants?.BACK_TO_LOGIN ?? 'Back to'}{' '}
                    <button
                      type="button"
                      className="theme-color btn-link p-0 border-0 bg-transparent text-decoration-underline fw-600"
                      onClick={handleBackToLogin}
                      disabled={isBackToLoginNavigating || showSignUpLoader}
                    >
                      {LanguageConstants?.LOGIN}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <footer className="auth-footer">
          <div className="auth-footer__lang">
            <LanguageDropdown instanceId="langDropdownCompleteProfile" />
          </div>
          <div className="auth-footer__copy">{LanguageConstants?.COPYRIGHT}</div>
          <div className="auth-footer__legal">
            <Link to="/cms/privacy-policy">{LanguageConstants?.PRIACYPOLICY}</Link>
            <Link to="/cms/terms-and-conditions">{LanguageConstants?.TERMS}</Link>
          </div>
        </footer>
      </div>
      ) : null}
    </>
  );
}

export default CompleteProfile;
